import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useNavigate } from "react-router";
import { useAuth } from "src/context/AuthContext";
import { useT } from "src/i18n";
import EmailStep from "src/components/auth/EmailStep";
import LoginFlow from "src/components/LoginFlow";
import RegisterFlow from "src/components/RegisterFlow";
import GlassDialog from "src/components/common/GlassDialog";
import { getCounts } from "src/utils/valut/valutApi";
import { captureError } from "src/utils/errorReporting";

// Inner component so probe resets on every reopen — MUI Dialog unmounts
// children when `open=false` without `keepMounted`.
//
// Flow (email-first, mode-agnostic — the entry button no longer picks a
// login-vs-register mode; the probe result alone decides the branch):
//   1. EmailStep — user enters email; we call checkEmail.
//   2a. If isAvailable=true  → RegisterFlow (skips its own email step).
//   2b. If isAvailable=false → LoginFlow with the right method picker.
function AuthBody() {
    const [email, setEmail] = useState("");
    const [probe, setProbe] = useState(null);

    if (!probe) {
        return (
            <EmailStep
                email={email}
                onEmailChange={setEmail}
                onResult={({ email: confirmed, isAvailable, hasPassword }) => {
                    setEmail(confirmed);
                    setProbe({ isAvailable, hasPassword });
                }}
            />
        );
    }

    if (probe.isAvailable) {
        return <RegisterFlow email={email} onBack={() => setProbe(null)} />;
    }

    return (
        <LoginFlow
            email={email}
            hasPassword={probe.hasPassword}
            onBack={() => setProbe(null)}
        />
    );
}

export default function AuthModal({ open, onClose, onSuccess }) {
    const { isAuthenticated, token } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const t = useT();

    // Track the auth state at the moment the modal opened.
    const wasAuthAtOpen = useRef(isAuthenticated);
    const firedRef = useRef(false);
    // Drives the centered "Kirjaudutaan…" backdrop while the post-login probe
    // (getCounts → navigate) runs. Bridges the silent gap between the social
    // provider's setPending(null) and the modal actually closing.
    const [redirecting, setRedirecting] = useState(false);

    useEffect(() => {
        if (!open) {
            // Reset only when closing while still anonymous — closing while authenticated
            // shouldn't poison wasAuthAtOpen=true, otherwise the next open (e.g. from a
            // header logout→login bounce) would skip the false→true success-callback path.
            if (!isAuthenticated) wasAuthAtOpen.current = false;
            firedRef.current = false;
            // Reset on close so the next open doesn't flash a stale "Kirjaudutaan…" backdrop;
            // grouped with the ref resets above for a single close-time cleanup.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setRedirecting(false);
            return;
        }
        if (firedRef.current || wasAuthAtOpen.current || !isAuthenticated) return;

        firedRef.current = true;

        // When the caller passed onSuccess, defer to it — its callback decides
        // where the user goes next (e.g. the anonymous-Lähetä-then-auth claim flow).
        if (onSuccess) {
            onSuccess();
            onClose();
            return;
        }

        // Otherwise run the post-login redirect probe: drafts win, then sent rows,
        // else stay on the current route. Set redirecting here (not above) so the
        // "Kirjaudutaan…" backdrop only shows on this awaited path — the onSuccess
        // branch closes immediately and would never paint it.
        setRedirecting(true);
        (async () => {
            try {
                const counts = await getCounts({ token });
                const sentTotal = (counts.open ?? 0) + (counts.pending_verification ?? 0)
                    + (counts.accepted ?? 0) + (counts.cancelled ?? 0)
                    + (counts.expired ?? 0) + (counts.no_supply ?? 0);
                onClose();
                if ((counts.draft ?? 0) > 0) {
                    if (window.location.pathname !== "/tilaa") navigate("/tilaa");
                    return;
                }
                if (sentTotal > 0) {
                    if (window.location.pathname !== "/valut") navigate("/valut");
                }
            } catch (err) {
                captureError(err, { tags: { entity: "pumppuRequest", operation: "post-login-redirect-probe" } });
                onClose();
            }
        })();
    }, [open, isAuthenticated, token, onSuccess, onClose, navigate]);

    const w = theme.custom?.wizard ?? {};

    return (
        <GlassDialog
            open={open}
            onClose={onClose}
            contentSx={{ pb: 5, px: { xs: 3, sm: 5 }, position: "relative" }}
        >
            <AuthBody />
            {redirecting && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        bgcolor: "rgba(0,0,0,0.55)",
                        backdropFilter: "blur(6px)",
                        WebkitBackdropFilter: "blur(6px)",
                        borderRadius: w.cardRadius ? `${w.cardRadius}px` : undefined,
                        zIndex: 1,
                    }}
                >
                    <CircularProgress color="warning" />
                    <Typography variant="body2" sx={{ color: "text.primary" }}>
                        {t("auth.shared.loggingIn")}
                    </Typography>
                </Box>
            )}
        </GlassDialog>
    );
}
